import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ExternalLink, ScanText } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExportButton } from '@/components/common/ExportButton';
import { Snippet } from '@/components/common/Snippet';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { ThesaurusNotice } from '@/pages/cade/ThesaurusNotice';
import { cadeDocumentParams, fetchCadeDocuments } from '@/services/cade';
import type { CadeDocumentQuery, CadeDocumentRow, CadeDocumentSearch } from '@/services/types';

/** The milestone kinds a search can be narrowed to (analytics.cade.sei.DOC_KINDS). */
const KINDS = [
  'VOTE',
  'TECHNICAL_NOTE',
  'OPINION',
  'MPF_OPINION',
  'REPORT',
  'DECISION_DISPATCH',
  'OPENING',
  'SG_CLOSING',
  'JUDGMENT',
  'TCC',
  'ACC',
  'EMBARGOS',
] as const;

const MODES = ['hybrid', 'exact', 'meaning'] as const;

function queryFromUrl(params: URLSearchParams): CadeDocumentQuery {
  const mode = params.get('mode');
  const year = (key: string) => {
    const value = Number(params.get(key));
    return Number.isInteger(value) && value > 1989 ? value : undefined;
  };
  return {
    query: params.get('q') ?? undefined,
    mode: mode === 'exact' || mode === 'meaning' ? mode : 'hybrid',
    kind: params.get('kind') ?? undefined,
    year_from: year('from'),
    year_to: year('to'),
    process: params.get('process') ?? undefined,
  };
}

function urlFromQuery(q: CadeDocumentQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (q.query) params.set('q', q.query);
  if (q.mode && q.mode !== 'hybrid') params.set('mode', q.mode);
  if (q.kind) params.set('kind', q.kind);
  if (q.year_from) params.set('from', String(q.year_from));
  if (q.year_to) params.set('to', String(q.year_to));
  if (q.process) params.set('process', q.process);
  return params;
}

/**
 * Search over the text of CADE's SEI decision documents: the votes, technical notes and
 * opinions where the market definition, the theory of harm and the fine calculation are.
 *
 * Two retrievals, fused: the words typed (with the decision search's thesaurus, so THC2 also
 * finds what the votes call SSE) and the meaning of the query (an embedding). Every row says
 * which found it, because a passage found only by meaning carries none of the words typed, and
 * a passage read by OCR says so, because a scanned page can be misread. When search by meaning
 * is off in this environment the exact matches still answer and the screen says why.
 *
 * The URL carries the search (`?q=`, `mode=`, `kind=`, `from=`, `to=`, `process=`), so a
 * search can be shared and the export is the same query.
 */
export function CadeDocumentsPage() {
  const { t } = useTranslation(['cade', 'common']);
  const [searchParams, setSearchParams] = useSearchParams();
  const [draft, setDraft] = useState(searchParams.get('q') ?? '');
  const [query, setQuery] = useState<CadeDocumentQuery>(() => queryFromUrl(searchParams));

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['cade', 'documents', query],
    queryFn: () => fetchCadeDocuments(query),
    placeholderData: keepPreviousData,
  });

  const update = (next: Partial<CadeDocumentQuery>) =>
    setQuery((current) => {
      const merged = { ...current, ...next };
      setSearchParams(urlFromQuery(merged), { replace: true });
      return merged;
    });

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('cade:documents.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('cade:documents.subtitle')}</p>
      </header>

      {data ? <AsOfStrip asOf={data.as_of} /> : null}

      <form
        role="search"
        className="space-y-3 rounded-md border p-3"
        onSubmit={(event) => {
          event.preventDefault();
          update({ query: draft.trim() || undefined });
        }}
      >
        <div className="flex flex-wrap items-end gap-2">
          <label className="min-w-64 flex-1 space-y-1 text-xs text-muted-foreground">
            {t('cade:documents.query')}
            <input
              type="search"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder={t('cade:documents.placeholder')}
              className="block w-full rounded-md border bg-background px-3 py-1.5 text-sm text-foreground"
            />
          </label>
          <button
            type="submit"
            className="rounded-md bg-primary px-4 py-1.5 text-sm text-primary-foreground"
          >
            {t('cade:documents.search')}
          </button>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <label className="space-y-1 text-xs text-muted-foreground">
            {t('cade:documents.mode')}
            <select
              value={query.mode ?? 'hybrid'}
              onChange={(event) =>
                update({ mode: event.target.value as CadeDocumentQuery['mode'] })
              }
              className="block rounded-md border bg-background px-3 py-1.5 text-sm text-foreground"
            >
              {MODES.map((mode) => (
                <option key={mode} value={mode}>
                  {t(`cade:documents.modes.${mode}`)}
                </option>
              ))}
            </select>
          </label>
          <label className="space-y-1 text-xs text-muted-foreground">
            {t('cade:documents.kind')}
            <select
              value={query.kind ?? ''}
              onChange={(event) => update({ kind: event.target.value || undefined })}
              className="block rounded-md border bg-background px-3 py-1.5 text-sm text-foreground"
            >
              <option value="">{t('cade:documents.anyKind')}</option>
              {KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {t(`cade:case.sei.kinds.${kind}`)}
                </option>
              ))}
            </select>
          </label>
          <YearInput
            label={t('cade:documents.from')}
            value={query.year_from}
            onChange={(year_from) => update({ year_from })}
          />
          <YearInput
            label={t('cade:documents.to')}
            value={query.year_to}
            onChange={(year_to) => update({ year_to })}
          />
        </div>
      </form>

      <div className="flex justify-end gap-2">
        <ExportButton path={`/api/v1/cade/export/documents?${cadeDocumentParams(query)}`} />
        <ExportButton
          path={`/api/v1/cade/export/documents.docx?${cadeDocumentParams(query)}`}
          label={t('common:export.word')}
        />
      </div>

      {isPending ? (
        <SkeletonLoader rows={6} />
      ) : error ? (
        <ErrorMessage onRetry={() => void refetch()} />
      ) : (
        <Answer answer={data.search} scope="corpus" help={t('cade:documents.help')}>
          {(search: CadeDocumentSearch) => <Results search={search} />}
        </Answer>
      )}
    </div>
  );
}

function YearInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number | undefined;
  onChange: (year: number | undefined) => void;
}) {
  return (
    <label className="space-y-1 text-xs text-muted-foreground">
      {label}
      <input
        inputMode="numeric"
        defaultValue={value ?? ''}
        onBlur={(event) => {
          const year = Number(event.target.value);
          onChange(Number.isInteger(year) && year > 1989 && year < 2101 ? year : undefined);
        }}
        className="block w-24 rounded-md border bg-background px-3 py-1.5 text-sm text-foreground"
      />
    </label>
  );
}

function Results({ search }: { search: CadeDocumentSearch }) {
  const { t, i18n } = useTranslation(['cade']);
  const number = new Intl.NumberFormat(i18n.language);
  return (
    <div className="space-y-3">
      {search.no_searchable_terms ? (
        <p role="alert" className="rounded-md border border-amber-500/50 p-3 text-sm text-foreground">
          {t('cade:documents.noTerms')}
        </p>
      ) : null}
      {search.mode !== 'exact' && search.meaning_status !== 'available' ? (
        <p className="text-xs text-muted-foreground" data-meaning-status={search.meaning_status}>
          {t(`cade:documents.meaningOff.${search.meaning_status}`)}
        </p>
      ) : null}
      {!search.exact_ranked ? (
        <p role="status" className="rounded-md border p-3 text-sm text-foreground" data-unranked>
          {t('cade:documents.unranked')}
        </p>
      ) : null}
      <ThesaurusNotice groups={search.expansions} />
      <p className="text-xs text-muted-foreground" aria-live="polite" data-documents-summary>
        {search.exact_documents === null
          ? t('cade:documents.summaryUnranked', { shown: number.format(search.rows.length) })
          : t('cade:documents.summary', {
              shown: number.format(search.rows.length),
              exact: number.format(search.exact_documents),
            })}
      </p>
      <ol className="space-y-3">
        {search.rows.map((row) => (
          <li key={`${row.nup}-${row.sei_doc_number}`}>
            <DocumentCard row={row} />
          </li>
        ))}
      </ol>
    </div>
  );
}

function DocumentCard({ row }: { row: CadeDocumentRow }) {
  const { t, i18n } = useTranslation(['cade']);
  const day = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeZone: 'UTC' });
  return (
    <article className="space-y-2 rounded-lg border bg-card p-4" data-document-row={row.match}>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm">
        <span className="font-medium text-foreground">
          {row.kind ? t(`cade:case.sei.kinds.${row.kind}`) : row.doc_type}
        </span>
        {row.doc_date ? (
          <span className="tabular-nums text-muted-foreground">
            {day.format(new Date(`${row.doc_date}T00:00:00Z`))}
          </span>
        ) : null}
        {row.case_key ? (
          <Link
            to={`/cade/c/${row.case_key}`}
            className="tabular-nums text-primary underline underline-offset-4"
          >
            {row.nup}
          </Link>
        ) : (
          <span className="tabular-nums text-foreground">{row.nup}</span>
        )}
        {row.unit ? <span className="text-xs text-muted-foreground">{row.unit}</span> : null}
        <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-foreground">
          {t(`cade:documents.match.${row.match}`)}
        </span>
        {row.from_ocr ? (
          <span className="inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-xs text-foreground">
            <ScanText aria-hidden="true" className="size-3" />
            {t('cade:documents.ocr')}
          </span>
        ) : null}
      </div>
      <p className="text-sm text-foreground">
        <Snippet text={row.snippet} />
      </p>
      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
        <span>{t('cade:documents.page', { page: row.page })}</span>
        {row.url ? (
          <a
            href={row.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-primary underline underline-offset-4"
            title={
              row.read_on
                ? t('cade:case.sei.readOn', {
                    date: day.format(new Date(`${row.read_on}T00:00:00Z`)),
                  })
                : undefined
            }
          >
            {t('cade:documents.open', { number: row.sei_doc_number })}
            <ExternalLink aria-hidden="true" className="size-3" />
          </a>
        ) : null}
      </div>
    </article>
  );
}
